import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
} from '@mui/material';
import { RootState } from '../../store';
import { ElementTarget, Formulation } from '../../types/formulation';
import ElementTargetTable from './ElementTargetTable';
import { setFormulationName } from '../../store/slices/formulationSlice';

interface FormulationCreatorProps {
  onSave?: (formulation: Formulation) => void;
  initialFormulation?: Formulation;
  waterProfile?: any;
}

const defaultElements: { id: string; symbol: string; name: string; atomicWeight: number }[] = [
  { id: "1", symbol: "N", name: "Nitrogen", atomicWeight: 14.007 },
  { id: "2", symbol: "NO3", name: "Nitrate", atomicWeight: 14.007 },
  { id: "3", symbol: "NH4", name: "Ammonium", atomicWeight: 14.007 },
  { id: "4", symbol: "P", name: "Phosphorus", atomicWeight: 30.974 },
  { id: "5", symbol: "K", name: "Potassium", atomicWeight: 39.098 },
  { id: "6", symbol: "Ca", name: "Calcium", atomicWeight: 40.078 },
  { id: "7", symbol: "Mg", name: "Magnesium", atomicWeight: 24.305 },
  { id: "8", symbol: "S", name: "Sulfur", atomicWeight: 32.065 },
  { id: "9", symbol: "Fe", name: "Iron", atomicWeight: 55.845 },
  { id: "10", symbol: "Mn", name: "Manganese", atomicWeight: 54.938 },
  { id: "11", symbol: "Zn", name: "Zinc", atomicWeight: 65.38 },
  { id: "12", symbol: "B", name: "Boron", atomicWeight: 10.811 },
  { id: "13", symbol: "Cu", name: "Copper", atomicWeight: 63.546 },
  { id: "14", symbol: "Mo", name: "Molybdenum", atomicWeight: 95.95 },
  { id: "15", symbol: "Si", name: "Silicon", atomicWeight: 28.0855 },
];

const FormulationCreator: React.FC<FormulationCreatorProps> = ({
  onSave,
  initialFormulation,
  waterProfile,
}) => {
  const dispatch = useDispatch();
  const { name } = useSelector((state: RootState) => state.formulation);
  // Get water profiles and add ids if they're missing
  const rawProfiles = useSelector((state: RootState) => state.waterQuality.profiles);
  const waterProfiles = React.useMemo(() => {
    return rawProfiles.map((profile, index) => ({
      ...profile,
      id: (profile as { id?: string }).id || `profile-${index}`, // Use existing ID or generate one
    }));
  }, [rawProfiles]);
  
  const [formulation, setFormulation] = useState<Formulation>(
    initialFormulation || {
      name: name || "New Formulation",
      description: "",
      waterProfileId: "",
      targetEc: 1.8,
      targetPh: 5.8,
      volume: 20,
      volumeUnit: "L",
      elements: [],
    }
  );

  const [elements, setElements] = useState<Array<typeof defaultElements[0]>>([]);

  useEffect(() => {
    // Fetch elements from API or use default
    setElements(defaultElements);
  }, []);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value;
    setFormulation((prev) => ({ ...prev, name: newName }));
    dispatch(setFormulationName(newName));
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormulation((prev) => ({ ...prev, description: e.target.value }));
  };

  const handleWaterProfileChange = (e: SelectChangeEvent<string>) => {
    setFormulation((prev) => ({ ...prev, waterProfileId: e.target.value }));
  };

  const handleTargetChange = (field: keyof Formulation, value: any) => {
    setFormulation((prev) => ({ ...prev, [field]: value }));
  };

  const handleElementTargetsChange = (targets: ElementTarget[]) => {
    setFormulation((prev) => ({ ...prev, elements: targets }));
  };

  const handleSaveFormulation = () => {
    if (onSave) onSave(formulation);
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Formulation Name"
            value={formulation.name}
            onChange={handleNameChange}
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={formulation.description}
            onChange={handleDescriptionChange}
            fullWidth
            multiline
            rows={2}
          />
          <FormControl fullWidth>
            <InputLabel id="water-profile-label">Water Profile</InputLabel>
            <Select
              labelId="water-profile-label"
              id="water-profile-select"
              value={formulation.waterProfileId || ""}
              label="Water Profile"
              onChange={handleWaterProfileChange}
            >
              <MenuItem value="">
                <em>None (Pure Water)</em>
              </MenuItem>
              {waterProfiles.map((profile) => (
                <MenuItem key={profile.id} value={profile.id}>
                  {profile.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Target EC (mS/cm)"
              type="number"
              value={formulation.targetEc}
              onChange={(e) => handleTargetChange('targetEc', Number(e.target.value))}
              fullWidth
              InputProps={{
                inputProps: { min: 0, max: 5, step: 0.1 }
              }}
            />
            <TextField
              label="Target pH"
              type="number"
              value={formulation.targetPh}
              onChange={(e) => handleTargetChange('targetPh', Number(e.target.value))}
              fullWidth
              InputProps={{
                inputProps: { min: 5, max: 7, step: 0.1 }
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Volume"
              type="number"
              value={formulation.volume}
              onChange={(e) => handleTargetChange('volume', Number(e.target.value))}
              fullWidth
              InputProps={{
                inputProps: { min: 1, step: 1 }
              }}
            />
            <FormControl fullWidth>
              <InputLabel id="volume-unit-label">Unit</InputLabel>
              <Select
                labelId="volume-unit-label"
                id="volume-unit-select"
                value={formulation.volumeUnit}
                label="Unit"
                onChange={(e) => handleTargetChange('volumeUnit', e.target.value)}
              >
                <MenuItem value="L">Liters</MenuItem>
                <MenuItem value="gal">Gallons</MenuItem>
                <MenuItem value="m3">Cubic Meters</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Element Targets
        </Typography>
        <ElementTargetTable 
          targets={formulation.elements} 
          onChange={handleElementTargetsChange} 
          elements={elements}
        />
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button variant="contained" color="primary" onClick={handleSaveFormulation}>
          Save Formulation
        </Button>
      </Box>
    </Box>
  );
};

export default FormulationCreator;
